import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { 
  TrendingUp, 
  History, 
  Settings, 
  BarChart3, 
  Plus, 
  ArrowRight,
  RefreshCw,
  Play,
  Zap,
  LogOut,
  Target,
  Server,
  CheckCircle,
  Trash2,
  Calendar,
  Activity,
  Loader2,
  Palette,
  Search
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MainDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, changeTheme, allThemes, currentTheme } = useTheme();
  
  // Helper function to convert theme accent to color values
  const getAccentColor = (accent) => {
    const colorMap = {
      emerald: { rgb: '34, 197, 94', tailwind: 'emerald' },
      purple: { rgb: '168, 85, 247', tailwind: 'purple' },
      cyan: { rgb: '6, 182, 212', tailwind: 'cyan' },
      rose: { rgb: '244, 63, 94', tailwind: 'rose' },
      amber: { rgb: '251, 191, 36', tailwind: 'amber' },
      orange: { rgb: '249, 115, 22', tailwind: 'orange' },
      blue: { rgb: '59, 130, 246', tailwind: 'blue' },
      red: { rgb: '239, 68, 68', tailwind: 'red' },
      lime: { rgb: '132, 204, 22', tailwind: 'lime' },
      violet: { rgb: '139, 92, 246', tailwind: 'violet' },
      sky: { rgb: '14, 165, 233', tailwind: 'sky' },
      fuchsia: { rgb: '217, 70, 239', tailwind: 'fuchsia' },
      green: { rgb: '34, 197, 94', tailwind: 'green' },
      indigo: { rgb: '99, 102, 241', tailwind: 'indigo' },
      pink: { rgb: '236, 72, 153', tailwind: 'pink' },
      teal: { rgb: '20, 184, 166', tailwind: 'teal' },
    };
    return colorMap[accent] || colorMap.emerald;
  };
  
  // State
  const [loading, setLoading] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    predictedStocks: 0,
    historyStocks: 0,
    successfulStocks: 0,
    notSuccessfulStocks: 0,
    successRate: 0,
    notSuccessRate: 0
  });
  
  // Expanded card state
  const [expandedCard, setExpandedCard] = useState(null);
  
  // Add Stock Dialog
  const [addStockOpen, setAddStockOpen] = useState(false);
  const [newStock, setNewStock] = useState({ symbol: "", strategy: "BUY" });
  const [addingStock, setAddingStock] = useState(false);
  
  // Admin Action States
  const [runningPipeline, setRunningPipeline] = useState(false);
  const [fixingReviewDates, setFixingReviewDates] = useState(false);
  const [movingToHistory, setMovingToHistory] = useState(false);
  const [clearingFuturePrices, setClearingFuturePrices] = useState(false);
  const [liveMonitorStatus, setLiveMonitorStatus] = useState({ is_running: false, stocks_monitored: 0 });

  // Fetch dashboard stats
  useEffect(() => {
    fetchDashboardStats();
    fetchLiveMonitorStatus();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Add cache-busting timestamp
      const cacheBuster = Date.now();
      
      // ONLY fetch from pipeline/predictions (daily_predictions collection - single source of truth)
      const pipelineRes = await axios.get(`${API}/ai/pipeline/predictions?days=7&_t=${cacheBuster}`, {
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      
      const pipelineTrades = pipelineRes.data.trades || [];
      
      // Count unevaluated stocks
      const predictedStocks = pipelineTrades.filter(t => !t.is_evaluated).length;
      
      // Fetch history stats (fetch all history to match Trade History page with all dates selected)
      const historyRes = await axios.get(`${API}/ai/trade-history?days=3650&_t=${cacheBuster}`, {
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      const historyStocks = historyRes.data?.total || 0;
      const successfulStocks = historyRes.data?.successful || 0;
      const notSuccessfulStocks = historyRes.data?.not_successful || 0;
      const successRate = historyRes.data?.successful_pct || 0;
      const notSuccessRate = historyRes.data?.not_successful_pct || 0;
      
      setDashboardStats({
        predictedStocks,
        historyStocks,
        successfulStocks,
        notSuccessfulStocks,
        successRate,
        notSuccessRate
      });
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStock = async () => {
    if (!newStock.symbol.trim()) {
      toast.error("Please enter a stock symbol");
      return;
    }

    try {
      setAddingStock(true);
      await axios.post(`${API}/ai/custom-stock`, {
        symbol: newStock.symbol.toUpperCase(),
        strategy: newStock.strategy,
        force: true
      });

      toast.success(`Stock ${newStock.symbol} added successfully!`);
      setAddStockOpen(false);
      setNewStock({ symbol: "", strategy: "BUY" });
      fetchDashboardStats();
    } catch (error) {
      console.error("Error adding stock:", error);
      toast.error(error.response?.data?.detail || "Failed to add stock");
    } finally {
      setAddingStock(false);
    }
  };

  // Admin Action Functions
  const runPipeline = async () => {
    try {
      setRunningPipeline(true);
      toast.info("Starting AI Pipeline...");
      await axios.post(`${API}/ai/run-pipeline`, { run_all: false });
      toast.success("AI Pipeline completed successfully!");
      fetchDashboardStats();
    } catch (error) {
      console.error("Error running pipeline:", error);
      toast.error(error.response?.data?.detail || "Failed to run pipeline");
    } finally {
      setRunningPipeline(false);
    }
  };

  const fixReviewDates = async () => {
    try {
      setFixingReviewDates(true);
      toast.info("Fixing review dates...");
      const res = await axios.post(`${API}/ai/fix-review-dates`);
      toast.success(res.data.message || "Review dates fixed successfully!");
    } catch (error) {
      console.error("Error fixing review dates:", error);
      toast.error(error.response?.data?.detail || "Failed to fix review dates");
    } finally {
      setFixingReviewDates(false);
    }
  };

  const moveToHistory = async () => {
    try {
      setMovingToHistory(true);
      toast.info("Moving old stocks to history...");
      const res = await axios.post(`${API}/ai/move-to-history`);
      toast.success(res.data.message || "Stocks moved to history!");
      fetchDashboardStats();
    } catch (error) {
      console.error("Error moving to history:", error);
      toast.error(error.response?.data?.detail || "Failed to move stocks");
    } finally {
      setMovingToHistory(false);
    }
  };

  const clearFuturePrices = async () => {
    try {
      setClearingFuturePrices(true);
      toast.info("Clearing future prices...");
      const res = await axios.post(`${API}/ai/clear-future-prices`);
      toast.success(res.data.message || "Future prices cleared!");
    } catch (error) {
      console.error("Error clearing prices:", error);
      toast.error(error.response?.data?.detail || "Failed to clear prices");
    } finally {
      setClearingFuturePrices(false);
    }
  };

  const toggleLiveMonitor = async () => {
    try {
      const endpoint = liveMonitorStatus.is_running 
        ? `${API}/ai/live-monitor/stop`
        : `${API}/ai/live-monitor/start`;
      
      const res = await axios.post(endpoint);
      toast.success(res.data.message);
      fetchLiveMonitorStatus();
    } catch (error) {
      console.error("Error toggling live monitor:", error);
      toast.error(error.response?.data?.detail || "Failed to toggle monitor");
    }
  };

  const fetchLiveMonitorStatus = async () => {
    try {
      const res = await axios.get(`${API}/ai/live-monitor/status`);
      setLiveMonitorStatus(res.data);
    } catch (error) {
      console.error("Error fetching live monitor status:", error);
    }
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Animated Starfield Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-slate-950 to-black">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            radial-gradient(2px 2px at 20% 30%, rgba(59, 130, 246, 0.4), transparent),
            radial-gradient(2px 2px at 60% 70%, rgba(168, 85, 247, 0.4), transparent),
            radial-gradient(1px 1px at 50% 50%, rgba(34, 211, 238, 0.3), transparent),
            radial-gradient(1px 1px at 80% 10%, rgba(249, 115, 22, 0.3), transparent),
            radial-gradient(2px 2px at 90% 60%, rgba(236, 72, 153, 0.3), transparent),
            radial-gradient(1px 1px at 30% 80%, rgba(34, 211, 238, 0.2), transparent),
            radial-gradient(1px 1px at 70% 40%, rgba(59, 130, 246, 0.2), transparent)
          `,
          backgroundSize: '200% 200%',
          backgroundPosition: '50% 50%'
        }}></div>
      </div>

      {/* Left Side - Complex Graph Background (Cyan/Green) */}
      <div className="absolute left-0 top-20 bottom-0 w-32 opacity-25">
        {/* Vertical Candlestick Charts */}
        <div className="flex flex-col items-center justify-around h-full py-8">
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
            <div 
              key={`left-candle-${i}`}
              className="w-3 bg-gradient-to-t from-cyan-500 to-green-400 rounded-sm animate-pulse"
              style={{ 
                height: `${Math.random() * 60 + 20}%`,
                animationDelay: `${i * 0.2}s`,
                animationDuration: '2s'
              }}
            ></div>
          ))}
        </div>
        
        {/* Line Graph Overlay */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 400">
          <path 
            d="M 10 350 L 20 300 L 30 320 L 40 280 L 50 260 L 60 240 L 70 220 L 80 200 L 90 180" 
            stroke="rgba(34, 211, 238, 0.5)" 
            strokeWidth="2" 
            fill="none"
            className="animate-pulse"
          />
        </svg>
      </div>

      {/* Right Side - Complex Graph Background (Purple/Orange/Red) */}
      <div className="absolute right-0 top-20 bottom-0 w-32 opacity-25">
        {/* Vertical Candlestick Charts */}
        <div className="flex flex-col items-center justify-around h-full py-8">
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
            <div 
              key={`right-candle-${i}`}
              className="w-3 bg-gradient-to-t from-purple-500 via-orange-500 to-red-400 rounded-sm animate-pulse"
              style={{ 
                height: `${Math.random() * 60 + 20}%`,
                animationDelay: `${i * 0.3}s`,
                animationDuration: '2.5s'
              }}
            ></div>
          ))}
        </div>
        
        {/* Area Chart Overlay */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 400">
          <defs>
            <linearGradient id="redGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(239, 68, 68, 0.3)" />
              <stop offset="100%" stopColor="rgba(239, 68, 68, 0)" />
            </linearGradient>
          </defs>
          <path 
            d="M 10 350 L 20 320 L 30 300 L 40 280 L 50 260 L 60 240 L 70 220 L 80 200 L 90 180 L 90 400 L 10 400 Z" 
            fill="url(#redGradient)"
            className="animate-pulse"
            style={{animationDuration: '3s'}}
          />
        </svg>
      </div>

      {/* Center Background - More Graphs */}
      <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none">
        {/* Large Bar Chart */}
        <div className="flex items-end gap-2 h-64">
          {[40, 60, 45, 75, 50, 80, 65, 90, 70, 85].map((height, i) => (
            <div 
              key={`center-${i}`}
              className="w-6 bg-gradient-to-t from-blue-500 to-cyan-400 rounded-t animate-pulse"
              style={{ 
                height: `${height}%`,
                animationDelay: `${i * 0.1}s`,
                animationDuration: '3s'
              }}
            ></div>
          ))}
        </div>
      </div>

      {/* Header */}
      <div className="relative border-b border-slate-800/50 bg-black/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-lg shadow-lg shadow-cyan-500/50">
                <TrendingUp className="h-6 w-6 text-black" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">
                  StockSense <span className="text-cyan-400">AI</span>
                </h1>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-white">{user?.name || user?.username}</p>
                <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={logout}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Cards Grid - Show only when not expanded */}
        {!expandedCard && (
          <div className="relative px-8 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            
            {/* Card 1: Predicted Stocks */}
            <Card 
              className="relative overflow-hidden bg-black/40 backdrop-blur-sm border-4 cursor-pointer group h-64 rounded-2xl transition-all duration-300 hover:scale-[1.02]"
              style={{
                borderColor: `rgb(${getAccentColor(theme.predictedStocks.accent).rgb})`,
                boxShadow: `0 0 30px rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.6), 0 0 60px rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.3), inset 0 0 20px rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.1)`
              }}
              onClick={() => setExpandedCard('dashboard')}
            >
              {/* Complex Futuristic Background */}
              <div className="absolute inset-0">
                {/* Animated Grid */}
                <div className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage: `linear-gradient(rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.4) 1px, transparent 1px)`,
                    backgroundSize: '20px 20px'
                  }}
                ></div>
                
                {/* Multiple Candlestick Layers */}
                <div className="absolute inset-0 flex items-end justify-around px-4 pb-4 opacity-20">
                  {[25, 45, 35, 60, 50, 75, 65, 85, 70, 90, 80, 95, 88, 92].map((height, i) => (
                    <div key={i} className="w-2 rounded-sm animate-pulse"
                      style={{ 
                        height: `${height}%`, 
                        animationDelay: `${i * 0.1}s`, 
                        animationDuration: '2s',
                        background: `linear-gradient(to top, rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.8), rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.4))`
                      }}
                    ></div>
                  ))}
                </div>
                
                {/* Moving Average Lines */}
                <svg className="absolute inset-0 w-full h-full opacity-25" viewBox="0 0 300 200">
                  <path d="M 0 150 Q 50 120, 100 130 T 200 100 T 300 80" 
                    stroke={`rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.6)`} strokeWidth="2" fill="none" className="animate-pulse" />
                  <path d="M 0 160 Q 50 140, 100 150 T 200 130 T 300 110" 
                    stroke={`rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.4)`} strokeWidth="1.5" fill="none" className="animate-pulse" 
                    style={{animationDelay: '0.5s'}} />
                </svg>
                
                {/* Price Level Lines */}
                {[20, 40, 60, 80].map((y, i) => (
                  <div key={i} className="absolute left-0 right-0 h-px" 
                    style={{ top: `${y}%`, backgroundColor: `rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.2)` }}
                  ></div>
                ))}
                
                {/* Radial Glow */}
                <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl" 
                  style={{backgroundColor: `rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.1)`}}></div>
              </div>

              <div className="relative p-4 h-full flex flex-col z-10">
                <div className="mb-2 text-center">
                  <h3 className="text-2xl font-bold text-white mb-1">PREDICTED STOCKS</h3>
                  <div className="h-px w-full" style={{backgroundColor: `rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.4)`}}></div>
                </div>
                
                <p className="text-sm mb-2 text-center" style={{color: `rgb(${getAccentColor(theme.predictedStocks.accent).rgb})`}}>AI Picks Today</p>
                
                {/* Visual Chart Area */}
                <div className="flex-1 flex items-center justify-center relative min-h-0">
                  {/* Stock Prediction Targets - Multiple Symbols */}
                  <div className="absolute inset-0 flex items-center justify-around px-4">
                    {/* Symbol 1 - Bullish */}
                    <div className="flex flex-col items-center gap-1">
                      <div className="text-xs font-bold" style={{color: `rgb(${getAccentColor(theme.predictedStocks.accent).rgb})`}}>RELIANCE</div>
                      <svg width="40" height="50" viewBox="0 0 40 50">
                        <path d="M 20 45 L 20 10" stroke={`rgb(${getAccentColor(theme.predictedStocks.accent).rgb})`} strokeWidth="2" fill="none" />
                        <polygon points="20,5 15,15 25,15" fill={`rgb(${getAccentColor(theme.predictedStocks.accent).rgb})`} />
                        <circle cx="20" cy="35" r="3" fill={`rgb(${getAccentColor(theme.predictedStocks.accent).rgb})`} className="animate-pulse" />
                      </svg>
                      <div className="text-xs" style={{color: `rgb(${getAccentColor(theme.predictedStocks.accent).rgb})`}}>↑ 8%</div>
                    </div>
                    
                    {/* Symbol 2 - Strong Bullish */}
                    <div className="flex flex-col items-center gap-1">
                      <div className="text-xs font-bold" style={{color: `rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.9)`}}>TCS</div>
                      <svg width="40" height="50" viewBox="0 0 40 50">
                        <path d="M 20 45 L 20 5" stroke={`rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.9)`} strokeWidth="2.5" fill="none" />
                        <polygon points="20,2 14,14 26,14" fill={`rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.9)`} />
                        <circle cx="20" cy="38" r="3" fill={`rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.9)`} className="animate-pulse" style={{animationDelay: '0.2s'}} />
                      </svg>
                      <div className="text-xs" style={{color: `rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.9)`}}>↑ 12%</div>
                    </div>
                    
                    {/* Symbol 3 - Moderate */}
                    <div className="flex flex-col items-center gap-1">
                      <div className="text-xs font-bold" style={{color: `rgb(${getAccentColor(theme.predictedStocks.accent).rgb})`}}>INFY</div>
                      <svg width="40" height="50" viewBox="0 0 40 50">
                        <path d="M 20 45 L 20 15" stroke={`rgb(${getAccentColor(theme.predictedStocks.accent).rgb})`} strokeWidth="2" fill="none" />
                        <polygon points="20,10 15,20 25,20" fill={`rgb(${getAccentColor(theme.predictedStocks.accent).rgb})`} />
                        <circle cx="20" cy="37" r="3" fill={`rgb(${getAccentColor(theme.predictedStocks.accent).rgb})`} className="animate-pulse" style={{animationDelay: '0.4s'}} />
                      </svg>
                      <div className="text-xs" style={{color: `rgb(${getAccentColor(theme.predictedStocks.accent).rgb})`}}>↑ 10%</div>
                    </div>
                    
                    {/* Target Badge */}
                    <div className="absolute top-2 right-2 rounded-full px-3 py-1" 
                      style={{
                        backgroundColor: `rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.2)`,
                        borderWidth: '1px',
                        borderColor: `rgba(${getAccentColor(theme.predictedStocks.accent).rgb}, 0.4)`
                      }}>
                      <span className="text-xs font-bold" style={{color: `rgb(${getAccentColor(theme.predictedStocks.accent).rgb})`}}>AI PICKS</span>
                    </div>
                  </div>
                </div>

                <div className="mt-2 space-y-1">
                  <div className="flex items-center gap-2 justify-center">
                    <span className="text-3xl font-bold text-white">{dashboardStats.predictedStocks}</span>
                    <span className="text-base" style={{color: `rgb(${getAccentColor(theme.predictedStocks.accent).rgb})`}}>Stocks</span>
                  </div>
                  <div className="flex items-center gap-2 justify-center">
                    <span className="text-slate-400 text-xs">Confidence:</span>
                    <span className="text-lg font-bold" style={{color: `rgb(${getAccentColor(theme.predictedStocks.accent).rgb})`}}>100%</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Card 2: History */}
            <Card 
              className="relative overflow-hidden bg-black/40 backdrop-blur-sm border-4 cursor-pointer group h-64 rounded-2xl transition-all duration-300 hover:scale-[1.02]"
              style={{
                borderColor: `rgb(${getAccentColor(theme.history.accent).rgb})`,
                boxShadow: `0 0 30px rgba(${getAccentColor(theme.history.accent).rgb}, 0.6), 0 0 60px rgba(${getAccentColor(theme.history.accent).rgb}, 0.3), inset 0 0 20px rgba(${getAccentColor(theme.history.accent).rgb}, 0.1)`
              }}
              onClick={() => setExpandedCard('history')}
            >
              {/* Complex Futuristic Background */}
              <div className="absolute inset-0">
                {/* Animated Grid */}
                <div className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage: `linear-gradient(rgba(${getAccentColor(theme.history.accent).rgb}, 0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(${getAccentColor(theme.history.accent).rgb}, 0.4) 1px, transparent 1px)`,
                    backgroundSize: '20px 20px'
                  }}
                ></div>
                
                {/* Multiple Area Charts */}
                <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 300 200">
                  <defs>
                    <linearGradient id="historyBg1" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor={`rgba(${getAccentColor(theme.history.accent).rgb}, 0.3)`} />
                      <stop offset="100%" stopColor={`rgba(${getAccentColor(theme.history.accent).rgb}, 0)`} />
                    </linearGradient>
                    <linearGradient id="historyBg2" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor={`rgba(${getAccentColor(theme.history.accent).rgb}, 0.2)`} />
                      <stop offset="100%" stopColor={`rgba(${getAccentColor(theme.history.accent).rgb}, 0)`} />
                    </linearGradient>
                  </defs>
                  {/* Area 1 */}
                  <path d="M 0 140 Q 50 110, 100 120 T 200 90 T 300 70 L 300 200 L 0 200 Z" 
                    fill="url(#historyBg1)" className="animate-pulse" />
                  {/* Area 2 */}
                  <path d="M 0 160 Q 50 130, 100 140 T 200 110 T 300 90 L 300 200 L 0 200 Z" 
                    fill="url(#historyBg2)" className="animate-pulse" style={{animationDelay: '0.5s'}} />
                  {/* Lines */}
                  <path d="M 0 140 Q 50 110, 100 120 T 200 90 T 300 70" 
                    stroke={`rgba(${getAccentColor(theme.history.accent).rgb}, 0.5)`} strokeWidth="2" fill="none" />
                </svg>
                
                {/* Data Points */}
                {[30, 50, 70].map((x, i) => (
                  <div key={i} className="absolute w-2 h-2 rounded-full animate-pulse"
                    style={{ 
                      left: `${x}%`, 
                      top: `${40 + i * 10}%`, 
                      animationDelay: `${i * 0.3}s`,
                      backgroundColor: `rgb(${getAccentColor(theme.history.accent).rgb})`
                    }}
                  ></div>
                ))}
                
                {/* Radial Glow */}
                <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl"
                  style={{backgroundColor: `rgba(${getAccentColor(theme.history.accent).rgb}, 0.1)`}}></div>
              </div>

              <div className="relative p-4 h-full flex flex-col z-10">
                <div className="mb-2 text-center">
                  <h3 className="text-2xl font-bold text-white mb-1">HISTORY</h3>
                  <div className="h-px w-full" style={{backgroundColor: `rgba(${getAccentColor(theme.history.accent).rgb}, 0.4)`}}></div>
                </div>
                
                <p className="text-sm mb-2 text-center" style={{color: `rgb(${getAccentColor(theme.history.accent).rgb})`}}>Past Performance</p>
                
                {/* Visual Chart Area - Historical Performance Comparison */}
                <div className="flex-1 flex items-center justify-center relative min-h-0">
                  <svg width="100%" height="100" viewBox="0 0 400 100" className="absolute inset-0">
                    <defs>
                      <linearGradient id="historyGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor={`rgba(${getAccentColor(theme.history.accent).rgb}, 0.4)`} />
                        <stop offset="100%" stopColor={`rgba(${getAccentColor(theme.history.accent).rgb}, 0.0)`} />
                      </linearGradient>
                    </defs>
                    {/* Area fill - Historical Data */}
                    <path d="M 20 70 L 60 50 L 100 60 L 140 45 L 180 50 L 220 35 L 260 40 L 300 30 L 340 35 L 380 25 L 380 90 L 20 90 Z" fill="url(#historyGradient)" />
                    {/* Line - Performance Trend */}
                    <path d="M 20 70 L 60 50 L 100 60 L 140 45 L 180 50 L 220 35 L 260 40 L 300 30 L 340 35 L 380 25" 
                      stroke={`rgb(${getAccentColor(theme.history.accent).rgb})`}
                      strokeWidth="3" 
                      fill="none"
                      style={{filter: `drop-shadow(0 0 8px rgba(${getAccentColor(theme.history.accent).rgb}, 0.8))`}} />
                    {/* Key Performance Points */}
                    <circle cx="60" cy="50" r="2" fill="white" className="animate-pulse" />
                    <circle cx="220" cy="35" r="2" fill="white" className="animate-pulse" style={{animationDelay: '0.3s'}} />
                    <circle cx="340" cy="35" r="2" fill="white" className="animate-pulse" style={{animationDelay: '0.6s'}} />
                    
                    {/* Win/Loss Indicators */}
                    <text x="100" y="55" fill={`rgba(${getAccentColor(theme.history.accent).rgb}, 0.6)`} fontSize="10">W</text>
                    <text x="180" y="45" fill={`rgba(${getAccentColor(theme.history.accent).rgb}, 0.6)`} fontSize="10">W</text>
                    <text x="260" y="35" fill={`rgba(${getAccentColor(theme.history.accent).rgb}, 0.6)`} fontSize="10">L</text>
                  </svg>
                </div>

                <div className="mt-2">
                  <div className="flex items-center gap-2 justify-center">
                    <span className="text-slate-400 text-xs">Win Rate:</span>
                    <span className="text-2xl font-bold" style={{color: `rgb(${getAccentColor(theme.history.accent).rgb})`}}>{dashboardStats.successRate.toFixed(0)}%</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Card 3: Analytics */}
            <Card 
              className="relative overflow-hidden bg-black/40 backdrop-blur-sm border-4 cursor-pointer group h-64 rounded-2xl transition-all duration-300 hover:scale-[1.02]"
              style={{
                borderColor: `rgb(${getAccentColor(theme.analytics.accent).rgb})`,
                boxShadow: `0 0 30px rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.6), 0 0 60px rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.3), inset 0 0 20px rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.1)`
              }}
              onClick={() => setExpandedCard('analytics')}
            >
              {/* Complex Futuristic Background */}
              <div className="absolute inset-0">
                {/* Animated Grid */}
                <div className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage: `linear-gradient(rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.4) 1px, transparent 1px)`,
                    backgroundSize: '20px 20px'
                  }}
                ></div>
                
                {/* Bar Chart Background */}
                <div className="absolute inset-0 flex items-end justify-around px-6 pb-6 opacity-15">
                  {[30, 45, 60, 40, 70, 55, 80, 65, 75, 85, 70, 90].map((height, i) => (
                    <div key={i} className="w-2 rounded-sm animate-pulse"
                      style={{ 
                        height: `${height}%`, 
                        animationDelay: `${i * 0.15}s`, 
                        animationDuration: '2.5s',
                        background: `linear-gradient(to top, rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.8), rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.4))`
                      }}
                    ></div>
                  ))}
                </div>
                
                {/* Pie Chart Rings */}
                <svg className="absolute right-4 top-4 opacity-15" width="100" height="100" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="35" fill="none" stroke={`rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.3)`} strokeWidth="8" />
                  <circle cx="50" cy="50" r="35" fill="none" stroke={`rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.6)`} strokeWidth="8" 
                    strokeDasharray="150 220" transform="rotate(-90 50 50)" className="animate-pulse" />
                </svg>
                
                {/* Scatter Plot Points */}
                {[{x: 20, y: 30}, {x: 40, y: 50}, {x: 60, y: 40}, {x: 80, y: 70}].map((pos, i) => (
                  <div key={i} className="absolute w-1.5 h-1.5 rounded-full animate-pulse opacity-30"
                    style={{ 
                      left: `${pos.x}%`, 
                      top: `${pos.y}%`, 
                      animationDelay: `${i * 0.2}s`,
                      backgroundColor: `rgb(${getAccentColor(theme.analytics.accent).rgb})`
                    }}
                  ></div>
                ))}
                
                {/* Trend Lines */}
                <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 300 200">
                  <path d="M 0 180 L 100 140 L 200 100 L 300 60" 
                    stroke={`rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.5)`} strokeWidth="1.5" fill="none" className="animate-pulse" />
                  <path d="M 0 160 L 100 120 L 200 80 L 300 40" 
                    stroke={`rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.3)`} strokeWidth="1" fill="none" className="animate-pulse" 
                    style={{animationDelay: '0.5s'}} />
                </svg>
                
                {/* Radial Glow */}
                <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl"
                  style={{backgroundColor: `rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.1)`}}></div>
              </div>

              <div className="relative p-4 h-full flex flex-col z-10">
                <div className="mb-2 text-center">
                  <h3 className="text-2xl font-bold text-white mb-1">ANALYTICS</h3>
                  <div className="h-px w-full" style={{backgroundColor: `rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.4)`}}></div>
                </div>
                
                <p className="text-sm mb-1 text-center" style={{color: `rgb(${getAccentColor(theme.analytics.accent).rgb})`}}>Deep Insights</p>
                <p className="text-xs mb-2 text-center" style={{color: `rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.8)`}}>Sector Analysis</p>
                
                {/* Visual Charts Area - Analytics Dashboard */}
                <div className="grid grid-cols-2 gap-2" style={{height: '85px'}}>
                  {/* Sector Performance Bar Chart */}
                  <div className="flex items-end justify-around gap-1">
                    {[40, 60, 45, 75, 50, 80, 65, 90].map((height, i) => (
                      <div 
                        key={i}
                        className="w-full rounded-sm opacity-70 relative"
                        style={{ 
                          height: `${height}%`,
                          background: `linear-gradient(to top, rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.7), rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.4))`
                        }}
                      >
                        {i === 7 && <div className="absolute -top-3 left-0 right-0 text-center text-[8px]" style={{color: `rgb(${getAccentColor(theme.analytics.accent).rgb})`}}>TOP</div>}
                      </div>
                    ))}
                  </div>
                  
                  {/* Market Distribution Pie */}
                  <div className="flex items-center justify-center relative">
                    <svg width="70" height="70" viewBox="0 0 120 120">
                      <circle cx="60" cy="60" r="45" fill="none" stroke={`rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.3)`} strokeWidth="16" />
                      <circle cx="60" cy="60" r="45" fill="none" stroke={`rgb(${getAccentColor(theme.analytics.accent).rgb})`} strokeWidth="16" 
                        strokeDasharray={`${(dashboardStats.successRate / 100) * 282} 282`}
                        transform="rotate(-90 60 60)"
                        style={{filter: `drop-shadow(0 0 10px rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.8))`}} />
                      <text x="60" y="65" textAnchor="middle" fill={`rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.8)`} fontSize="16" fontWeight="bold">{dashboardStats.successRate.toFixed(0)}%</text>
                    </svg>
                  </div>
                </div>

                {/* Bottom Info - Clear spacing */}
                <div className="mt-auto pt-2 space-y-1">
                  <div className="flex items-center gap-2 justify-center">
                    <span className="text-2xl font-bold" style={{color: `rgb(${getAccentColor(theme.analytics.accent).rgb})`}}>24 hrs</span>
                  </div>
                  <div className="flex gap-1.5 items-center justify-center">
                    <span className="text-xs" style={{color: `rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.9)`}}>AI</span>
                    <span className="text-xs" style={{color: `rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.6)`}}>and</span>
                    <span className="text-xs" style={{color: `rgba(${getAccentColor(theme.analytics.accent).rgb}, 0.7)`}}>Machine Learning</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Card 4: Admin */}
            <Card 
              className="relative overflow-hidden bg-black/40 backdrop-blur-sm border-4 cursor-pointer group h-64 rounded-2xl transition-all duration-300 hover:scale-[1.02]"
              style={{
                borderColor: `rgb(${getAccentColor(theme.admin.accent).rgb})`,
                boxShadow: `0 0 30px rgba(${getAccentColor(theme.admin.accent).rgb}, 0.6), 0 0 60px rgba(${getAccentColor(theme.admin.accent).rgb}, 0.3), inset 0 0 20px rgba(${getAccentColor(theme.admin.accent).rgb}, 0.1)`
              }}
              onClick={() => setExpandedCard('admin')}
            >
              {/* Complex Futuristic Background */}
              <div className="absolute inset-0">
                {/* Animated Grid */}
                <div className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage: `linear-gradient(rgba(${getAccentColor(theme.admin.accent).rgb}, 0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(${getAccentColor(theme.admin.accent).rgb}, 0.4) 1px, transparent 1px)`,
                    backgroundSize: '20px 20px'
                  }}
                ></div>
                
                {/* Network/System Diagram */}
                <svg className="absolute inset-0 w-full h-full opacity-15" viewBox="0 0 300 200">
                  {/* Connection Lines */}
                  <line x1="50" y1="50" x2="150" y2="100" stroke={`rgba(${getAccentColor(theme.admin.accent).rgb}, 0.4)`} strokeWidth="1" className="animate-pulse" />
                  <line x1="150" y1="100" x2="250" y2="50" stroke={`rgba(${getAccentColor(theme.admin.accent).rgb}, 0.4)`} strokeWidth="1" className="animate-pulse" />
                  <line x1="50" y1="150" x2="150" y2="100" stroke={`rgba(${getAccentColor(theme.admin.accent).rgb}, 0.4)`} strokeWidth="1" className="animate-pulse" />
                  <line x1="250" y1="150" x2="150" y2="100" stroke={`rgba(${getAccentColor(theme.admin.accent).rgb}, 0.4)`} strokeWidth="1" className="animate-pulse" />
                  {/* Nodes */}
                  <circle cx="50" cy="50" r="8" fill={`rgba(${getAccentColor(theme.admin.accent).rgb}, 0.3)`} stroke={`rgba(${getAccentColor(theme.admin.accent).rgb}, 0.6)`} strokeWidth="2" className="animate-pulse" />
                  <circle cx="250" cy="50" r="8" fill={`rgba(${getAccentColor(theme.admin.accent).rgb}, 0.3)`} stroke={`rgba(${getAccentColor(theme.admin.accent).rgb}, 0.6)`} strokeWidth="2" className="animate-pulse" style={{animationDelay: '0.3s'}} />
                  <circle cx="150" cy="100" r="12" fill={`rgba(${getAccentColor(theme.admin.accent).rgb}, 0.4)`} stroke={`rgba(${getAccentColor(theme.admin.accent).rgb}, 0.8)`} strokeWidth="2" className="animate-pulse" />
                  <circle cx="50" cy="150" r="8" fill={`rgba(${getAccentColor(theme.admin.accent).rgb}, 0.3)`} stroke={`rgba(${getAccentColor(theme.admin.accent).rgb}, 0.6)`} strokeWidth="2" className="animate-pulse" style={{animationDelay: '0.6s'}} />
                  <circle cx="250" cy="150" r="8" fill={`rgba(${getAccentColor(theme.admin.accent).rgb}, 0.3)`} stroke={`rgba(${getAccentColor(theme.admin.accent).rgb}, 0.6)`} strokeWidth="2" className="animate-pulse" style={{animationDelay: '0.9s'}} />
                </svg>
                
                {/* Data Flow Particles */}
                {[{x: 10, y: 20}, {x: 30, y: 60}, {x: 70, y: 40}, {x: 90, y: 80}].map((pos, i) => (
                  <div key={i} className="absolute w-1 h-1 rounded-full animate-pulse opacity-40"
                    style={{ 
                      left: `${pos.x}%`, 
                      top: `${pos.y}%`, 
                      animationDelay: `${i * 0.25}s`,
                      backgroundColor: `rgb(${getAccentColor(theme.admin.accent).rgb})`
                    }}
                  ></div>
                ))}
                
                {/* Hexagon Pattern */}
                <div className="absolute right-8 bottom-8 opacity-10">
                  <svg width="80" height="80" viewBox="0 0 100 100">
                    <polygon points="50,5 90,30 90,70 50,95 10,70 10,30" 
                      fill="none" stroke={`rgba(${getAccentColor(theme.admin.accent).rgb}, 0.5)`} strokeWidth="2" className="animate-pulse" />
                    <polygon points="50,25 75,40 75,60 50,75 25,60 25,40" 
                      fill="none" stroke={`rgba(${getAccentColor(theme.admin.accent).rgb}, 0.6)`} strokeWidth="2" className="animate-pulse" style={{animationDelay: '0.5s'}} />
                  </svg>
                </div>
                
                {/* Radial Glow */}
                <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl"
                  style={{backgroundColor: `rgba(${getAccentColor(theme.admin.accent).rgb}, 0.1)`}}></div>
              </div>

              <div className="relative p-4 h-full flex flex-col z-10">
                <div className="mb-2 text-center">
                  <h3 className="text-2xl font-bold text-white mb-1">ADMIN</h3>
                  <div className="h-px w-full" style={{backgroundColor: `rgba(${getAccentColor(theme.admin.accent).rgb}, 0.4)`}}></div>
                </div>
                
                <p className="text-sm mb-2 text-center" style={{color: `rgb(${getAccentColor(theme.admin.accent).rgb})`}}>System Controls</p>
                
                {/* Visual Icons Area - System Control */}
                <div className="flex-1 flex items-center justify-around min-h-0">
                  {/* Server/Database Stack */}
                  <div className="flex flex-col gap-2 items-center">
                    <div className="w-10 h-4 rounded-lg opacity-70 relative"
                      style={{background: `linear-gradient(to bottom, rgba(${getAccentColor(theme.admin.accent).rgb}, 0.8), rgba(${getAccentColor(theme.admin.accent).rgb}, 1))`}}>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-1 h-1 bg-white rounded-full animate-pulse"></div>
                      </div>
                    </div>
                    <div className="w-10 h-4 rounded-lg opacity-70 relative"
                      style={{background: `linear-gradient(to bottom, rgba(${getAccentColor(theme.admin.accent).rgb}, 0.8), rgba(${getAccentColor(theme.admin.accent).rgb}, 1))`}}>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-1 h-1 bg-white rounded-full animate-pulse" style={{animationDelay: '0.3s'}}></div>
                      </div>
                    </div>
                    <div className="text-[8px]" style={{color: `rgb(${getAccentColor(theme.admin.accent).rgb})`}}>DB</div>
                  </div>
                  
                  {/* Settings/Control Gear */}
                  <div className="relative flex flex-col items-center gap-1">
                    <Settings className="h-12 w-12 animate-spin" style={{color: `rgb(${getAccentColor(theme.admin.accent).rgb})`, animationDuration: '8s'}} />
                    <div className="absolute inset-0 flex items-center justify-center top-0">
                      <div className="w-4 h-4 rounded-full" style={{backgroundColor: `rgb(${getAccentColor(theme.admin.accent).rgb})`}}></div>
                    </div>
                    <div className="text-[8px]" style={{color: `rgb(${getAccentColor(theme.admin.accent).rgb})`}}>SYS</div>
                  </div>
                  
                  {/* Security Shield */}
                  <div className="flex flex-col items-center gap-1">
                    <svg width="40" height="45" viewBox="0 0 60 70" style={{color: `rgb(${getAccentColor(theme.admin.accent).rgb})`}}>
                      <path d="M 30 5 L 50 15 L 50 35 Q 50 55 30 65 Q 10 55 10 35 L 10 15 Z" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        fill={`rgba(${getAccentColor(theme.admin.accent).rgb}, 0.2)`}
                        style={{filter: `drop-shadow(0 0 10px rgba(${getAccentColor(theme.admin.accent).rgb}, 0.6))`}} />
                      <circle cx="30" cy="35" r="6" fill="currentColor" className="animate-pulse" />
                      <path d="M 27 35 L 29 37 L 33 33" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" />
                    </svg>
                    <div className="text-[8px]" style={{color: `rgb(${getAccentColor(theme.admin.accent).rgb})`}}>SEC</div>
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="flex items-center gap-2 justify-center" style={{color: `rgb(${getAccentColor(theme.admin.accent).rgb})`}}>
                    <span className="text-sm">▸</span>
                    <span className="text-xs">Model Tuning</span>
                  </div>
                  <div className="flex items-center gap-2 justify-center" style={{color: `rgb(${getAccentColor(theme.admin.accent).rgb})`}}>
                    <span className="text-sm">▸</span>
                    <span className="text-xs">Data Management</span>
                  </div>
                </div>
              </div>


            {/* Card 5: Simulation Trace Engine - Spans 2 columns */}
            <Card 
              className="relative overflow-hidden bg-black/40 backdrop-blur-sm border-4 cursor-pointer group h-64 rounded-2xl transition-all duration-300 hover:scale-[1.02] md:col-span-2"
              style={{
                borderColor: 'rgb(147, 51, 234)',
                boxShadow: '0 0 30px rgba(147, 51, 234, 0.6), 0 0 60px rgba(147, 51, 234, 0.3), inset 0 0 20px rgba(147, 51, 234, 0.1)'
              }}
              onClick={() => navigate('/simulation-trace')}
            >
              <div className="absolute inset-0">
                <div className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage: 'linear-gradient(rgba(147, 51, 234, 0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(147, 51, 234, 0.4) 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                  }}
                ></div>
                <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl" 
                  style={{backgroundColor: 'rgba(147, 51, 234, 0.1)'}}></div>
              </div>

              <div className="relative p-4 h-full flex flex-col z-10">
                <div className="mb-2 text-center">
                  <h3 className="text-2xl font-bold text-white mb-1">SIMULATION TRACE ENGINE</h3>
                  <div className="h-px w-full" style={{backgroundColor: 'rgba(147, 51, 234, 0.4)'}}></div>
                </div>
                
                <p className="text-sm mb-2 text-center" style={{color: 'rgb(147, 51, 234)'}}>Audit & Replay Pipeline</p>
                
                <div className="flex-1 flex items-center justify-center gap-6">
                  <div className="flex flex-col items-center gap-2">
                    <Search className="h-10 w-10" style={{color: 'rgb(147, 51, 234)'}} />
                    <div className="text-xs" style={{color: 'rgb(147, 51, 234)'}}>Trace</div>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <Activity className="h-10 w-10 animate-pulse" style={{color: 'rgb(147, 51, 234)'}} />
                    <div className="text-xs" style={{color: 'rgb(147, 51, 234)'}}>Monitor</div>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <CheckCircle className="h-10 w-10" style={{color: 'rgb(147, 51, 234)'}} />
                    <div className="text-xs" style={{color: 'rgb(147, 51, 234)'}}>Verify</div>
                  </div>
                </div>

                <div className="mt-auto pt-2">
                  <div className="text-center">
                    <span className="text-sm font-semibold" style={{color: 'rgb(147, 51, 234)'}}>Deep Simulation & Explainable AI</span>
                  </div>
                </div>
              </div>
            </Card>

            </Card>

          </div>
          </div>
        )}

        {/* Expanded Card Views */}
        {expandedCard && (
          <div className="space-y-4 relative">
            <Button 
              onClick={() => setExpandedCard(null)}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
              Back to Dashboard
            </Button>

            {/* Dashboard Expanded */}
            {expandedCard === 'dashboard' && (
              <Card className="bg-slate-900/50 border-slate-800">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-cyan-500/10 rounded-lg">
                        <Target className="h-6 w-6 text-cyan-400" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">Predicted Stocks</h2>
                        <p className="text-sm text-slate-400">Add and manage stock predictions</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Add Stock Form */}
                  <Card className="bg-slate-800/50 border-slate-700 p-6 mb-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Add New Stock</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="stock-symbol" className="text-slate-300">Stock Symbol</Label>
                        <Input
                          id="stock-symbol"
                          placeholder="e.g., RELIANCE.NS"
                          value={newStock.symbol}
                          onChange={(e) => setNewStock({ ...newStock, symbol: e.target.value.toUpperCase() })}
                          className="mt-1 bg-slate-900 border-slate-700 text-white"
                        />
                      </div>
                      <div>
                        <Label htmlFor="stock-strategy" className="text-slate-300">Strategy</Label>
                        <select
                          id="stock-strategy"
                          value={newStock.strategy}
                          onChange={(e) => setNewStock({ ...newStock, strategy: e.target.value })}
                          className="mt-1 w-full bg-slate-900 border border-slate-700 text-white rounded-md px-3 py-2"
                        >
                          <option value="BUY">BUY</option>
                          <option value="SHORT">SHORT</option>
                        </select>
                      </div>
                      <div className="flex items-end">
                        <Button
                          onClick={handleAddStock}
                          disabled={addingStock || !newStock.symbol.trim()}
                          className="w-full bg-cyan-600 hover:bg-cyan-700"
                        >
                          {addingStock ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Adding...
                            </>
                          ) : (
                            <>
                              <Plus className="h-4 w-4 mr-2" />
                              Add Stock
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </Card>

                  {/* Predicted Stocks List - Iframe */}
                  <div className="border border-slate-700 rounded-lg overflow-hidden" style={{height: '600px'}}>
                    <iframe
                      src="/dashboard"
                      className="w-full h-full"
                      title="Predicted Stocks List"
                    />
                  </div>
                </div>
              </Card>
            )}

            {/* History Expanded */}
            {expandedCard === 'history' && (
              <Card className="bg-slate-900/50 border-slate-800">
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-purple-500/10 rounded-lg">
                      <History className="h-6 w-6 text-purple-400" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">Trade History</h2>
                      <p className="text-sm text-slate-400">View all evaluated trades</p>
                    </div>
                  </div>
                  
                  <div className="h-[600px] border border-slate-700 rounded-lg overflow-hidden">
                    <iframe
                      src="/ai-history"
                      className="w-full h-full"
                      title="History"
                    />
                  </div>
                </div>
              </Card>
            )}

            {/* Admin Expanded */}
            {expandedCard === 'admin' && (
              <Card className="bg-slate-900/50 border-slate-800">
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-orange-500/10 rounded-lg">
                      <Settings className="h-6 w-6 text-orange-400" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">Admin Functions</h2>
                      <p className="text-sm text-slate-400">System management and controls</p>
                    </div>
                  </div>
                  
                  {/* Navigation & Views */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wide">Navigation & Views</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <Card 
                        className="bg-slate-800/50 border-slate-700 p-6 cursor-pointer hover:border-purple-500/50 transition-all"
                        onClick={() => navigate("/ai-history")}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <History className="h-5 w-5 text-purple-400" />
                          <h3 className="font-semibold text-white">Trade History</h3>
                        </div>
                        <p className="text-sm text-slate-400">View evaluated trade outcomes</p>
                      </Card>

                      <Card 
                        className="bg-slate-800/50 border-slate-700 p-6 cursor-pointer hover:border-emerald-500/50 transition-all"
                        onClick={() => navigate("/performance")}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <BarChart3 className="h-5 w-5 text-emerald-400" />
                          <h3 className="font-semibold text-white">Analytics</h3>
                        </div>
                        <p className="text-sm text-slate-400">Performance insights and metrics</p>
                      </Card>

                      <Card 
                        className="bg-slate-800/50 border-slate-700 p-6 cursor-pointer hover:border-cyan-500/50 transition-all"
                        onClick={() => setAddStockOpen(true)}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <Plus className="h-5 w-5 text-cyan-400" />
                          <h3 className="font-semibold text-white">Add Stock</h3>
                        </div>
                        <p className="text-sm text-slate-400">Manually add stock predictions</p>
                      </Card>
                    </div>
                  </div>

                  {/* Pipeline & Jobs */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wide">Pipeline & Jobs</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <Card 
                        className="bg-slate-800/50 border-slate-700 p-6 cursor-pointer hover:border-orange-500/50 transition-all"
                        onClick={() => navigate("/batch-jobs")}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <Settings className="h-5 w-5 text-orange-400" />
                          <h3 className="font-semibold text-white">Batch Jobs</h3>
                        </div>
                        <p className="text-sm text-slate-400">Scheduled pipeline tasks</p>
                      </Card>

                      <Card 
                        className="bg-slate-800/50 border-slate-700 p-6 cursor-pointer hover:border-orange-500/50 transition-all"
                        onClick={() => navigate("/machine-learning")}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <Zap className="h-5 w-5 text-orange-400" />
                          <h3 className="font-semibold text-white">ML AI Pipeline</h3>
                        </div>
                        <p className="text-sm text-slate-400">Machine learning insights</p>
                      </Card>

                      <Card 
                        className="bg-slate-800/50 border-slate-700 p-6 cursor-pointer hover:border-yellow-500/50 transition-all"
                        onClick={() => navigate("/dashboard")}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <Zap className="h-5 w-5 text-yellow-400" />
                          <h3 className="font-semibold text-white">Connect Zerodha</h3>
                        </div>
                        <p className="text-sm text-slate-400">Trading platform integration</p>
                      </Card>
                    </div>
                  </div>

                  {/* Admin Actions */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wide">Admin Actions</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <Button
                        onClick={runPipeline}
                        disabled={runningPipeline}
                        className="h-auto p-6 bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 border-0 flex-col items-start gap-2"
                      >
                        <div className="flex items-center gap-2 w-full">
                          {runningPipeline ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <Play className="h-5 w-5" />
                          )}
                          <span className="font-semibold">Run AI Pipeline</span>
                        </div>
                        <span className="text-xs text-blue-100/80">Execute full prediction workflow</span>
                      </Button>

                      <Button
                        onClick={fixReviewDates}
                        disabled={fixingReviewDates}
                        className="h-auto p-6 bg-gradient-to-br from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 border-0 flex-col items-start gap-2"
                      >
                        <div className="flex items-center gap-2 w-full">
                          {fixingReviewDates ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <Calendar className="h-5 w-5" />
                          )}
                          <span className="font-semibold">Fix Review Dates</span>
                        </div>
                        <span className="text-xs text-cyan-100/80">Correct weekend/holiday dates</span>
                      </Button>

                      <Button
                        onClick={moveToHistory}
                        disabled={movingToHistory}
                        className="h-auto p-6 bg-gradient-to-br from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 border-0 flex-col items-start gap-2"
                      >
                        <div className="flex items-center gap-2 w-full">
                          {movingToHistory ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <CheckCircle className="h-5 w-5" />
                          )}
                          <span className="font-semibold">Move to History</span>
                        </div>
                        <span className="text-xs text-amber-100/80">Archive evaluated stocks</span>
                      </Button>

                      <Button
                        onClick={clearFuturePrices}
                        disabled={clearingFuturePrices}
                        className="h-auto p-6 bg-gradient-to-br from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 border-0 flex-col items-start gap-2"
                      >
                        <div className="flex items-center gap-2 w-full">
                          {clearingFuturePrices ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <Trash2 className="h-5 w-5" />
                          )}
                          <span className="font-semibold">Clear Future Prices</span>
                        </div>
                        <span className="text-xs text-red-100/80">Remove incorrect future data</span>
                      </Button>

                      <Button
                        onClick={toggleLiveMonitor}
                        className={`h-auto p-6 border-0 flex-col items-start gap-2 ${
                          liveMonitorStatus.is_running
                            ? "bg-gradient-to-br from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800"
                            : "bg-gradient-to-br from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800"
                        }`}
                      >
                        <div className="flex items-center gap-2 w-full">
                          <Activity className="h-5 w-5" />
                          <span className="font-semibold">
                            {liveMonitorStatus.is_running ? "Live Monitor ON" : "Live Monitor OFF"}
                          </span>
                        </div>
                        <span className="text-xs text-white/80">
                          {liveMonitorStatus.is_running 
                            ? `Monitoring ${liveMonitorStatus.stocks_monitored} stocks`
                            : "Start real-time price monitoring"
                          }
                        </span>
                      </Button>

                      <Button
                        onClick={() => navigate('/simulation-trace')}
                        className="h-auto p-6 bg-gradient-to-br from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 border-0 flex-col items-start gap-2"
                      >
                        <div className="flex items-center gap-2 w-full">
                          <Search className="h-5 w-5" />
                          <span className="font-semibold">Simulation Trace Engine</span>
                        </div>
                        <span className="text-xs text-purple-100/80">Deep audit & replay pipeline</span>
                      </Button>

                      <Card className="bg-slate-800/50 border-slate-700 p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <Server className="h-5 w-5 text-green-400" />
                          <h3 className="font-semibold text-white">Deploy Status</h3>
                        </div>
                        <p className="text-sm text-green-400 font-medium">● Running</p>
                        <p className="text-xs text-slate-500 mt-1">Preview Environment</p>
                      </Card>
                    </div>
                  </div>

                  {/* Theme Settings */}
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Palette className="h-4 w-4 text-slate-400" />
                      <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wide">Theme Settings</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                      {Object.keys(allThemes).map((themeKey) => {
                        const themeOption = allThemes[themeKey];
                        const isActive = currentTheme === themeKey;
                        
                        return (
                          <Card
                            key={themeKey}
                            onClick={() => {
                              changeTheme(themeKey);
                              toast.success(`Theme changed to ${themeOption.name}`);
                            }}
                            className={`
                              cursor-pointer transition-all duration-300 p-4 relative overflow-hidden
                              ${themeOption.cardBase} 
                              ${isActive ? themeOption.cardBorder + ' ring-2 ring-offset-2 ring-offset-slate-900' : 'border-slate-700/50'}
                              hover:scale-105 ${themeOption.cardHover}
                            `}
                          >
                            {isActive && (
                              <div className="absolute top-2 right-2">
                                <Badge variant="outline" className={themeOption.predictedStocks.badge}>
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Active
                                </Badge>
                              </div>
                            )}
                            
                            <div className="space-y-3">
                              <h4 className={`font-semibold ${themeOption.textPrimary}`}>{themeOption.name}</h4>
                              <p className={`text-xs ${themeOption.textMuted}`}>{themeOption.description}</p>
                              
                              {/* Color Preview Dots */}
                              <div className="flex gap-2 pt-2">
                                <div className={`w-4 h-4 rounded-full bg-gradient-to-br ${themeOption.predictedStocks.gradient} border ${themeOption.predictedStocks.border}`} title="Predicted Stocks" />
                                <div className={`w-4 h-4 rounded-full bg-gradient-to-br ${themeOption.history.gradient} border ${themeOption.history.border}`} title="History" />
                                <div className={`w-4 h-4 rounded-full bg-gradient-to-br ${themeOption.analytics.gradient} border ${themeOption.analytics.border}`} title="Analytics" />
                                <div className={`w-4 h-4 rounded-full bg-gradient-to-br ${themeOption.admin.gradient} border ${themeOption.admin.border}`} title="Admin" />
                              </div>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Analytics Expanded */}
            {expandedCard === 'analytics' && (
              <Card className="bg-slate-900/50 border-slate-800">
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-emerald-500/10 rounded-lg">
                      <BarChart3 className="h-6 w-6 text-emerald-400" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">Performance Analytics</h2>
                      <p className="text-sm text-slate-400">Trade performance insights and metrics</p>
                    </div>
                  </div>
                  
                  <div className="h-[600px] border border-slate-700 rounded-lg overflow-hidden">
                    <iframe
                      src="/performance"
                      className="w-full h-full"
                      title="Performance Analytics"
                    />
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Add Stock Dialog */}
      <Dialog open={addStockOpen} onOpenChange={setAddStockOpen}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Add Custom Stock</DialogTitle>
            <DialogDescription className="text-slate-400">
              Manually add a stock prediction with full AI evaluation
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="stock-symbol" className="text-slate-300">Stock Symbol</Label>
              <Input
                id="stock-symbol"
                placeholder="e.g., RELIANCE.NS or INFY.BO"
                value={newStock.symbol}
                onChange={(e) => setNewStock({ ...newStock, symbol: e.target.value.toUpperCase() })}
                className="mt-1 bg-slate-800 border-slate-700 text-white"
              />
            </div>
            
            <div>
              <Label htmlFor="strategy" className="text-slate-300">Trading Strategy</Label>
              <select
                id="strategy"
                value={newStock.strategy}
                onChange={(e) => setNewStock({ ...newStock, strategy: e.target.value })}
                className="mt-1 w-full bg-slate-800 border border-slate-700 text-white rounded-md px-3 py-2"
              >
                <option value="BUY">BUY</option>
                <option value="SHORT">SHORT</option>
              </select>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddStockOpen(false)}
              className="border-slate-700 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddStock}
              disabled={addingStock || !newStock.symbol.trim()}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {addingStock ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Stock
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
